'use client';

import * as React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { useRenewalPipelineReport, type RenewalPipelineRow } from './useRenewalPipelineReport';

export interface RenewalPipelineReportProps {
  asOfDate: string;
}

type BucketKey = '30_days' | '60_days' | '90_days' | 'beyond';

const BUCKET_LABELS: Record<BucketKey, string> = {
  '30_days': '0-30 Days',
  '60_days': '31-60 Days',
  '90_days': '61-90 Days',
  beyond: '91+ Days',
};

const BUCKET_ORDER: BucketKey[] = ['30_days', '60_days', '90_days', 'beyond'];

/** Bucket boundaries on days_to_renewal (inclusive on both ends): 0-30, 31-60, 61-90, 91+. Matches get_renewal_pipeline_report's documented bucketing exactly. */
function bucketFor(daysToRenewal: number): BucketKey {
  if (daysToRenewal <= 30) return '30_days';
  if (daysToRenewal <= 60) return '60_days';
  if (daysToRenewal <= 90) return '90_days';
  return 'beyond';
}

function groupByBucket(rows: RenewalPipelineRow[]): Record<BucketKey, RenewalPipelineRow[]> {
  const groups: Record<BucketKey, RenewalPipelineRow[]> = {
    '30_days': [],
    '60_days': [],
    '90_days': [],
    beyond: [],
  };
  for (const row of rows) {
    groups[bucketFor(row.days_to_renewal)].push(row);
  }
  return groups;
}

function BucketSkeleton() {
  return (
    <div className="rounded-lg border border-border-primary bg-background-primary p-4">
      <Skeleton className="h-8 w-12" />
      <Skeleton className="mt-2 h-4 w-20" />
    </div>
  );
}

/** 30/60/90/beyond-day renewal buckets, driven entirely by useRenewalPipelineReport -- no hardcoded policy list. */
export function RenewalPipelineReport({ asOfDate }: RenewalPipelineReportProps) {
  const { data, isLoading, error } = useRenewalPipelineReport(asOfDate);

  if (isLoading) {
    return (
      <div
        className="grid grid-cols-2 gap-3 sm:grid-cols-4"
        aria-label="Loading renewal pipeline report"
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <BucketSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-700">
        Failed to load renewal pipeline report.
      </div>
    );
  }

  const rows = data ?? [];
  const groups = groupByBucket(rows);

  return (
    <div data-testid="renewal-pipeline-buckets">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" role="list">
        {BUCKET_ORDER.map((bucket) => (
          <div
            key={bucket}
            className="rounded-lg border border-border-primary bg-background-primary p-4"
            role="listitem"
            data-testid={`renewal-bucket-${bucket}`}
          >
            <p className="text-2xl font-semibold">{groups[bucket].length}</p>
            <p className="mt-1 text-sm font-medium">{BUCKET_LABELS[bucket]}</p>
          </div>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-text-secondary">No upcoming renewals.</p>
      ) : null}
    </div>
  );
}
